import type {
  RadioBooleanFilterValue,
  RadioDateFilterValue,
  RadioNumberFilterValue,
  RadioTextFilterValue,
  UserAttributeType,
} from '@hotjar/global-filters';
import {
  RadioBooleanFilterOperation,
  RadioDateFilterOperation,
  RadioNumberFilterOperation,
  RadioTextFilterOperation,
} from '@hotjar/global-filters';
import { UserAttributeKind } from '@hotjar/global-filters/src/domains/user-attributes/model/UserAttribute';
import {
  decodeDate,
  parseNumber,
  undelimit,
} from '../../../../../common/Responses/Filters/Query/queryUtils';
import {
  IS_GREATER_THAN,
  IS_LESS_THAN,
  IS_EXACTLY,
  IS_NOT,
  IS_BETWEEN,
  IS_UNKNOWN,
  HAS_ANY_VALUE,
  CONTAINS,
  NOT_CONTAINS,
  STARTS_WITH,
  NOT_STARTS_WITH,
  ENDS_WITH,
  NOT_ENDS_WITH,
  IS,
  IS_NOT_EXACTLY,
  REGEX,
  IS_TRUE,
  IS_FALSE,
  IS_EXACTLY_DAYS_AGO,
  IS_MORE_THAN_DAYS_AGO,
  IS_LESS_THAN_DAYS_AGO,
  IS_ON,
  IS_AFTER,
  IS_BEFORE,
  AND,
} from '../../../../../common/Responses/Filters/Query/GlobalFilterQuery/constants';

function parseNumberUserAttributeValue([operation, ...parts]: string[]):
  | RadioNumberFilterValue
  | undefined {
  switch (operation) {
    case IS_GREATER_THAN: {
      const value = parseNumber(parts[0]);
      if (value === undefined) return undefined;
      return { operation: RadioNumberFilterOperation.IS_GREATER_THAN, value };
    }
    case IS_LESS_THAN: {
      const value = parseNumber(parts[0]);
      if (value === undefined) return undefined;
      return { operation: RadioNumberFilterOperation.IS_LESS_THAN, value };
    }
    case IS_EXACTLY: {
      const value = parseNumber(parts[0]);
      if (value === undefined) return undefined;
      return { operation: RadioNumberFilterOperation.IS_EXACTLY, value };
    }
    case IS_NOT: {
      const value = parseNumber(parts[0]);
      if (value === undefined) return undefined;
      return { operation: RadioNumberFilterOperation.IS_NOT, value };
    }
    case IS_BETWEEN: {
      if (parts[1] !== AND) return undefined;
      const from = parseNumber(parts[0]);
      const to = parseNumber(parts[2]);
      if (from === undefined) return undefined;
      if (to === undefined) return undefined;
      return {
        operation: RadioNumberFilterOperation.IS_BETWEEN,
        from,
        to,
      };
    }
    case IS_UNKNOWN:
      return { operation: RadioNumberFilterOperation.IS_UNKNOWN };
    case HAS_ANY_VALUE:
      return { operation: RadioNumberFilterOperation.HAS_ANY_VALUE };
  }
  return undefined;
}

function parseStringUserAttributeValue([operation, value]: string[]):
  | RadioTextFilterValue
  | undefined {
  switch (operation) {
    case IS_UNKNOWN:
      return { operation: RadioTextFilterOperation.IS_UNKNOWN };
    case HAS_ANY_VALUE:
      return { operation: RadioTextFilterOperation.HAS_ANY_VALUE };
  }

  if (value.length === 0) return undefined;

  switch (operation) {
    case CONTAINS:
      return { operation: RadioTextFilterOperation.CONTAINS, value };
    case NOT_CONTAINS:
      return { operation: RadioTextFilterOperation.NOT_CONTAINS, value };
    case STARTS_WITH:
      return { operation: RadioTextFilterOperation.STARTS_WITH, value };
    case NOT_STARTS_WITH:
      return { operation: RadioTextFilterOperation.NOT_STARTS_WITH, value };
    case ENDS_WITH:
      return { operation: RadioTextFilterOperation.ENDS_WITH, value };
    case NOT_ENDS_WITH:
      return { operation: RadioTextFilterOperation.NOT_ENDS_WITH, value };
    case IS:
      return { operation: RadioTextFilterOperation.IS, value };
    case IS_NOT:
      return { operation: RadioTextFilterOperation.IS_NOT, value };
    case IS_EXACTLY:
      return { operation: RadioTextFilterOperation.IS_EXACTLY, value };
    case IS_NOT_EXACTLY:
      return { operation: RadioTextFilterOperation.IS_NOT_EXACTLY, value };
    case REGEX:
      return { operation: RadioTextFilterOperation.REGEX, value };
  }

  return undefined;
}

function parseBooleanUserAttributeValue([operation]: string[]):
  | RadioBooleanFilterValue
  | undefined {
  switch (operation) {
    case IS_TRUE:
      return { operation: RadioBooleanFilterOperation.IS_TRUE };
    case IS_FALSE:
      return { operation: RadioBooleanFilterOperation.IS_FALSE };
    case IS_UNKNOWN:
      return { operation: RadioBooleanFilterOperation.IS_UNKNOWN };
    case HAS_ANY_VALUE:
      return { operation: RadioBooleanFilterOperation.HAS_ANY_VALUE };
  }

  return undefined;
}

function parseDateUserAttributeValue([operation, value]: string[]):
  | RadioDateFilterValue
  | undefined {
  switch (operation) {
    case IS_EXACTLY_DAYS_AGO: {
      const num = parseNumber(value);
      if (num === undefined) return undefined;
      return { operation: RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO, value: num };
    }
    case IS_MORE_THAN_DAYS_AGO: {
      const num = parseNumber(value);
      if (num === undefined) return undefined;
      return {
        operation: RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
        value: num,
      };
    }
    case IS_LESS_THAN_DAYS_AGO: {
      const num = parseNumber(value);
      if (num === undefined) return undefined;
      return {
        operation: RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
        value: num,
      };
    }
    case IS_ON: {
      const date = decodeDate(value);
      if (date === undefined) return undefined;
      return {
        operation: RadioDateFilterOperation.IS_ON,
        value: date,
      };
    }
    case IS_AFTER: {
      const date = decodeDate(value);
      if (date === undefined) return undefined;
      return {
        operation: RadioDateFilterOperation.IS_AFTER,
        value: date,
      };
    }
    case IS_BEFORE: {
      const date = decodeDate(value);
      if (date === undefined) return undefined;
      return {
        operation: RadioDateFilterOperation.IS_BEFORE,
        value: date,
      };
    }
    case IS_UNKNOWN:
      return { operation: RadioDateFilterOperation.IS_UNKNOWN };
    case HAS_ANY_VALUE:
      return { operation: RadioDateFilterOperation.HAS_ANY_VALUE };
  }
  return undefined;
}

export function parseUserAttribute(paramValue: string): UserAttributeType | undefined {
  const [name, kind, ...parts] = undelimit(paramValue);
  if (name.length === 0) return undefined;
  switch (kind) {
    case 'number': {
      const value = parseNumberUserAttributeValue(parts);
      if (value === undefined) return undefined;
      return {
        kind: UserAttributeKind.Number,
        name,
        value,
      };
    }
    case 'string': {
      const value = parseStringUserAttributeValue(parts);
      if (value === undefined) return undefined;
      return {
        kind: UserAttributeKind.String,
        name,
        value,
      };
    }
    case 'email': {
      const value = parseStringUserAttributeValue(parts);
      if (value === undefined) return undefined;
      return {
        kind: UserAttributeKind.Email,
        name,
        value,
      };
    }
    case 'boolean': {
      const value = parseBooleanUserAttributeValue(parts);
      if (value === undefined) return undefined;
      return {
        kind: UserAttributeKind.Boolean,
        name,
        value,
      };
    }
    case 'date': {
      const value = parseDateUserAttributeValue(parts);
      if (value === undefined) return undefined;
      return {
        kind: UserAttributeKind.Date,
        name,
        value,
      };
    }
  }

  return undefined;
}
